<template>
  <div class="daily-shipment-monitoring nx-list-page">
    <section class="topbar">
      <div class="title-wrap">
        <div class="title">汽运入库管理-每日发运动态实时监控</div>
        <div class="subtitle">数据时间：{{ lastRefreshTime || '--' }}</div>
      </div>
      <div class="top-actions">
        <span :class="['status-pill', autoRefresh ? 'is-running' : 'is-paused']">
          <span class="dot"></span>{{ autoRefresh ? '自动刷新已开启' : '自动刷新已暂停' }}
        </span>
        <el-button size="small" @click="toggleAutoRefresh">
          {{ autoRefresh ? '暂停刷新' : '开启刷新' }}
        </el-button>
        <el-button size="small" type="primary" icon="el-icon-refresh" :loading="refreshing" @click="manualRefresh">
          立即刷新
        </el-button>
      </div>
    </section>

    <section class="metrics">
      <el-card class="metric-card" shadow="hover">
        <div class="metric-head">
          <span>今日发运总量</span>
          <span class="metric-icon blue">吨</span>
        </div>
        <div class="metric-value">{{ formatNumber(summary.netWeight) }}<span>吨</span></div>
        <div class="metric-foot">
          <span :class="trendClass(summary.amountYesterdayRate)">{{ trendText(summary.amountYesterdayRate) }}</span>
          <span>同比昨日</span>
        </div>
      </el-card>
      <el-card class="metric-card" shadow="hover">
        <div class="metric-head">
          <span>今日车辆总数</span>
          <span class="metric-icon green">车</span>
        </div>
        <div class="metric-value">{{ formatNumber(summary.truckNum) }}<span>辆</span></div>
        <div class="metric-foot">
          <span :class="trendClass(summary.truckNumYesterdayRate)">{{ trendText(summary.truckNumYesterdayRate) }}</span>
          <span>同比昨日</span>
        </div>
      </el-card>
      <el-card class="metric-card" shadow="hover">
        <div class="metric-head">
          <span>计划完成率</span>
          <span class="metric-icon orange">%</span>
        </div>
        <div class="metric-value">{{ formatRate(summary.planExecuteRate) }}<span>%</span></div>
        <div class="metric-foot">
          <span :class="trendClass(summary.planExcuteYesterdayRate)">
            {{ trendText(summary.planExcuteYesterdayRate, '百分点') }}
          </span>
          <span>较昨日</span>
        </div>
      </el-card>
      <el-card class="metric-card" shadow="hover">
        <div class="metric-head">
          <span>平均在厂时长</span>
          <span class="metric-icon cyan">时</span>
        </div>
        <div class="metric-value">{{ formatRate(summary.avgStayMinutes) }}<span>分钟</span></div>
        <div class="metric-foot">
          <span :class="trendClass(summary.avgStayYesterdayRate, true)">
            {{ trendText(summary.avgStayYesterdayRate) }}
          </span>
          <span>同比昨日</span>
        </div>
      </el-card>
    </section>

    <section class="main-layout">
      <el-card class="panel-card" shadow="never">
        <div slot="header" class="panel-head">
          <span class="panel-title">地磅实时数据</span>
          <div class="panel-tools">
            <el-input
              v-model="poundQuery.workstationName"
              size="small"
              clearable
              placeholder="按地磅位置/编码查询"
              prefix-icon="el-icon-search"
              @keyup.enter.native="searchPounds"
              @clear="searchPounds"
            />
            <el-button size="small" type="primary" icon="el-icon-search" @click="searchPounds">查询</el-button>
          </div>
        </div>
        <div v-loading="poundLoading">
          <div class="pound-grid">
            <div v-for="item in poundData.slice(0, 4)" :key="item.grossPoundNo || item.workstationName" class="pound-card">
              <div class="pound-name">
                <span :title="item.workstationName">{{ item.workstationName || item.grossPoundNo || '-' }}</span>
                <el-tag :type="statusType(item.workstationStatus)" size="mini">{{ statusText(item.workstationStatus) }}</el-tag>
              </div>
              <div class="pound-meta"><span>今日车辆</span><strong>{{ item.truckNum || 0 }} 辆</strong></div>
              <div class="pound-meta"><span>当前车辆</span><strong>{{ item.vehicle || '-' }}</strong></div>
              <div class="pound-meta"><span>最新过磅</span><strong>{{ timeText(item.grossTime) }}</strong></div>
            </div>
          </div>
          <el-table :data="poundData" max-height="260" border>
            <el-table-column prop="workstationName" label="地磅位置" min-width="180" show-overflow-tooltip />
            <el-table-column label="状态" width="100">
              <template slot-scope="{ row }">
                <el-tag :type="statusType(row.workstationStatus)" size="mini">{{ statusText(row.workstationStatus) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="truckNum" label="今日累计车辆数" width="140" align="right" />
            <el-table-column prop="vehicle" label="当前车辆" width="120" show-overflow-tooltip />
            <el-table-column label="最新过磅时间" width="170">
              <template slot-scope="{ row }">{{ timeText(row.grossTime) }}</template>
            </el-table-column>
          </el-table>
          <el-pagination
            class="table-pagination"
            background
            layout="total, sizes, prev, pager, next"
            :current-page="poundPage.currentPage"
            :page-size="poundPage.pageSize"
            :page-sizes="[6, 10, 20]"
            :total="poundPage.total"
            @size-change="poundSizeChange"
            @current-change="poundCurrentChange"
          />
        </div>
      </el-card>

      <el-card class="panel-card" shadow="never">
        <div slot="header" class="panel-head">
          <span class="panel-title">计划执行热度</span>
          <el-select v-model="riskLevel" size="small" clearable placeholder="全部风险">
            <el-option label="正常" value="normal" />
            <el-option label="关注" value="focus" />
            <el-option label="滞后" value="lag" />
          </el-select>
        </div>
        <el-table class="plan-heat-table" v-loading="planLoading" :data="filteredPlanData" max-height="340" border>
          <el-table-column prop="ownerName" label="货主" min-width="130" align="center" header-align="center" show-overflow-tooltip />
          <el-table-column label="计划量" width="96" align="center" header-align="center">
            <template slot-scope="{ row }">{{ formatNumber(row.planTotalWeight) }}</template>
          </el-table-column>
          <el-table-column label="已发量" width="96" align="center" header-align="center">
            <template slot-scope="{ row }">{{ formatNumber(row.doneWeight) }}</template>
          </el-table-column>
          <el-table-column label="完成进度" min-width="190" align="center" header-align="center" class-name="plan-progress-column">
            <template slot-scope="{ row }">
              <div class="progress-cell">
                <el-progress :percentage="progressPercent(row.completeRate)" :stroke-width="8" :show-text="false" />
                <span>{{ formatRate(row.completeRate) }}%</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="风险" width="76" align="center" header-align="center">
            <template slot-scope="{ row }">
              <el-tag :type="riskType(row.riskLevel)" size="mini">{{ riskText(row.riskLevel) }}</el-tag>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </section>

    <el-card class="panel-card detail-panel" shadow="never">
      <div slot="header" class="panel-head detail-head">
        <span class="panel-title">过车明细</span>
        <div class="detail-filters">
          <el-date-picker
            v-model="detailDateRange"
            size="small"
            type="daterange"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            @change="handleDetailDateChange"
          />
          <el-select v-model="detailQuery.custNo" size="small" clearable filterable placeholder="全部货主">
            <el-option v-for="item in custNoList" :key="item.id" :label="item.deptName" :value="item.id" />
          </el-select>
          <el-input
            v-model="detailQuery.trainNumber"
            size="small"
            clearable
            placeholder="车牌号"
            prefix-icon="el-icon-search"
            @keyup.enter.native="searchDetails"
          />
          <el-select
            v-model="detailQuery.cargoNo"
            size="small"
            filterable
            remote
            clearable
            reserve-keyword
            placeholder="全部物资"
            :remote-method="remoteMethod"
            :loading="dictLoading"
          >
            <el-option v-for="item in options" :key="item.varNo" :label="item.varName" :value="item.varNo" />
          </el-select>
          <el-button size="small" type="primary" icon="el-icon-search" @click="searchDetails">查询</el-button>
          <el-button size="small" icon="el-icon-refresh-left" @click="resetDetails">重置</el-button>
        </div>
      </div>
      <el-table v-loading="detailLoading" :data="detailData" border>
        <el-table-column prop="trainNumber" label="车牌号" width="110" show-overflow-tooltip />
        <el-table-column prop="custName" label="货主" min-width="150" show-overflow-tooltip />
        <el-table-column prop="cargoName" label="物资名称" width="130" show-overflow-tooltip />
        <el-table-column prop="heavyTime" label="重车时间" width="170" />
        <el-table-column prop="emptyTime" label="空车时间" width="170" />
        <el-table-column prop="tareWeight" label="皮重(吨)" width="100" align="right" />
        <el-table-column prop="grossWeight" label="毛重(吨)" width="100" align="right" />
        <el-table-column prop="netWeight" label="净重(吨)" width="100" align="right" />
        <el-table-column label="在厂时长" width="120">
          <template slot-scope="{ row }">{{ stayText(row.plantStaySeconds) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="{ row }">
            <el-tag :type="vehicleStatusType(row.vehicleStatus)" size="mini">{{ vehicleStatusText(row.vehicleStatus) }}</el-tag>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="table-pagination"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page="detailPage.currentPage"
        :page-size="detailPage.pageSize"
        :page-sizes="[10, 20, 50]"
        :total="detailPage.total"
        @size-change="detailSizeChange"
        @current-change="detailCurrentChange"
      />
    </el-card>
  </div>
</template>

<script>
import {
  getCustNoList,
  getSysParamPage,
  planSummary,
  pycode,
  selectTruckTotalByPoundPage,
  shipAmountTotal,
  transportPlanPage,
  weightPlan
} from './api'
import { getSettingDate } from '@/util/date'
import { unwrapDictList } from '@/util/dict-list'

const DEFAULT_REFRESH_INTERVAL_SECONDS = 5 * 60
const MIN_REFRESH_INTERVAL_SECONDS = 30
const MAX_REFRESH_INTERVAL_SECONDS = 60 * 60
const REFRESH_INTERVAL_PARAM_NAME = 'tin.daily.refresh_sec'

export default {
  name: 'car-storage-daily-shipment-monitoring',
  data () {
    return {
      refreshing: false,
      autoRefresh: true,
      refreshTimer: null,
      refreshIntervalSeconds: DEFAULT_REFRESH_INTERVAL_SECONDS,
      lastRefreshTime: '',
      summary: {},
      poundLoading: false,
      poundData: [],
      poundQuery: {
        workstationName: '',
        startTime: getSettingDate('dayS'),
        endTime: getSettingDate('dayE')
      },
      poundPage: {
        pageSize: 6,
        currentPage: 1,
        total: 0
      },
      planLoading: false,
      planData: [],
      riskLevel: '',
      detailLoading: false,
      detailData: [],
      detailDateRange: [
        getSettingDate('daySLast7', { format: 'YYYY-MM-DD' }),
        getSettingDate('dayE', { format: 'YYYY-MM-DD' })
      ],
      detailDateChanged: false,
      detailQuery: {
        custNo: '',
        trainNumber: '',
        cargoNo: ''
      },
      detailPage: {
        pageSize: 10,
        currentPage: 1,
        total: 0
      },
      options: [],
      custNoList: [],
      dictLoading: false
    }
  },
  computed: {
    filteredPlanData () {
      if (!this.riskLevel) return this.planData
      return this.planData.filter(item => item.riskLevel === this.riskLevel)
    }
  },
  created () {
    this.loadDictionaries()
    this.loadAll()
    this.loadRefreshInterval().finally(() => {
      this.startAutoRefresh()
    })
  },
  beforeDestroy () {
    this.clearAutoRefresh()
  },
  methods: {
    todayParams () {
      return {
        startTime: getSettingDate('dayS'),
        endTime: getSettingDate('dayE')
      }
    },
    yesterdayParams () {
      return this.dayOffsetParams(-1)
    },
    dayOffsetParams (offset) {
      const date = new Date()
      date.setDate(date.getDate() + offset)
      const month = `${date.getMonth() + 1}`.padStart(2, '0')
      const day = `${date.getDate()}`.padStart(2, '0')
      const dateText = `${date.getFullYear()}-${month}-${day}`
      return {
        startTime: `${dateText} 00:00:00`,
        endTime: `${dateText} 23:59:59`
      }
    },
    updateLastRefreshTime () {
      this.lastRefreshTime = getSettingDate('now')
    },
    async loadAll () {
      this.refreshing = true
      await Promise.all([
        this.loadSummary(),
        this.loadPounds(),
        this.loadPlanHeat(),
        this.loadDetails()
      ]).finally(() => {
        this.refreshing = false
        this.updateLastRefreshTime()
      })
    },
    loadDictionaries () {
      this.remoteMethod('')
      getCustNoList().then(res => {
        this.custNoList = unwrapDictList(res)
      }).catch(() => {
        this.custNoList = []
      })
    },
    loadRefreshInterval () {
      return getSysParamPage({
        page: 1,
        size: 1,
        paramKey: REFRESH_INTERVAL_PARAM_NAME
      }).then(res => {
        const pageData = res.data && res.data.data ? res.data.data : {}
        const records = Array.isArray(pageData.records) ? pageData.records : []
        const value = records.length ? records[0].paramValue : DEFAULT_REFRESH_INTERVAL_SECONDS
        this.refreshIntervalSeconds = this.normalizeRefreshIntervalSeconds(value)
      }).catch(() => {
        this.refreshIntervalSeconds = DEFAULT_REFRESH_INTERVAL_SECONDS
      })
    },
    loadSummary () {
      const params = this.todayParams()
      const yesterdayParams = this.yesterdayParams()
      return Promise.all([
        shipAmountTotal(params),
        planSummary(params),
        weightPlan({ page: 1, size: 200 }, params),
        shipAmountTotal(yesterdayParams),
        planSummary(yesterdayParams),
        weightPlan({ page: 1, size: 200 }, yesterdayParams)
      ]).then(([summaryRes, planRes, detailRes, yesterdaySummaryRes, yesterdayPlanRes, yesterdayDetailRes]) => {
        const data = (summaryRes.data && summaryRes.data.data) || {}
        const planData = (planRes.data && planRes.data.data) || {}
        const detailData = (detailRes.data && detailRes.data.data) || {}
        const yesterdayData = (yesterdaySummaryRes.data && yesterdaySummaryRes.data.data) || {}
        const yesterdayPlanData = (yesterdayPlanRes.data && yesterdayPlanRes.data.data) || {}
        const yesterdayDetailData = (yesterdayDetailRes.data && yesterdayDetailRes.data.data) || {}
        const records = detailData.records || []
        const yesterdayRecords = yesterdayDetailData.records || []
        const planTotalWeight = Number(planData.planTotalWeight || 0)
        const yesterdayPlanTotalWeight = Number(yesterdayPlanData.planTotalWeight || 0)
        const netWeight = Number(data.netWeight || 0)
        const yesterdayNetWeight = Number(yesterdayData.netWeight || 0)
        const truckNum = Number(data.truckNum || 0)
        const yesterdayTruckNum = Number(yesterdayData.truckNum || 0)
        const planExecuteRate = this.calcPercent(netWeight, planTotalWeight)
        const yesterdayPlanExecuteRate = this.calcPercent(yesterdayNetWeight, yesterdayPlanTotalWeight)
        const avgStayMinutes = this.calcAvgStayMinutes(records)
        const yesterdayAvgStayMinutes = this.calcAvgStayMinutes(yesterdayRecords)
        this.summary = {
          ...data,
          amountYesterdayRate: this.calcComparePercent(netWeight, yesterdayNetWeight),
          truckNumYesterdayRate: this.calcComparePercent(truckNum, yesterdayTruckNum),
          planTotalWeight,
          planExecuteRate,
          planExcuteYesterdayRate: Number((planExecuteRate - yesterdayPlanExecuteRate).toFixed(2)),
          avgStayMinutes,
          avgStayYesterdayRate: this.calcComparePercent(avgStayMinutes, yesterdayAvgStayMinutes)
        }
      }).catch(() => {
        this.summary = {}
        this.$message.error('今日发运汇总加载失败')
      })
    },
    loadPounds () {
      this.poundLoading = true
      const workstationName = this.poundQuery.workstationName ? String(this.poundQuery.workstationName).trim() : ''
      const params = {
        ...this.todayParams(),
        workstationName,
        page: this.poundPage.currentPage,
        size: this.poundPage.pageSize
      }
      return selectTruckTotalByPoundPage(params).then(res => {
        const data = (res.data && res.data.data) || {}
        const records = data.records || []
        const filteredRecords = this.filterPoundRecords(records, workstationName)
        this.poundData = filteredRecords
        this.poundPage.total = workstationName ? filteredRecords.length : (data.total || 0)
      }).catch(() => {
        this.poundData = []
        this.poundPage.total = 0
        this.$message.error('地磅实时数据加载失败')
      }).finally(() => {
        this.poundLoading = false
      })
    },
    filterPoundRecords (records, keyword) {
      if (!keyword) return records
      const lowerKeyword = keyword.toLowerCase()
      return records.filter(item => {
        const workstationName = item.workstationName ? String(item.workstationName).toLowerCase() : ''
        const heavyPoundNo = item.heavyPoundNo ? String(item.heavyPoundNo).toLowerCase() : ''
        const grossPoundNo = item.grossPoundNo ? String(item.grossPoundNo).toLowerCase() : ''
        return workstationName.includes(lowerKeyword) ||
          heavyPoundNo.includes(lowerKeyword) ||
          grossPoundNo.includes(lowerKeyword)
      })
    },
    loadPlanHeat () {
      this.planLoading = true
      const params = {
        ...this.todayParams(),
        page: 1,
        size: 100,
        status: 1
      }
      return transportPlanPage(params).then(res => {
        const data = (res.data && res.data.data) || {}
        const records = data.records || []
        this.planData = records.map(this.buildPlanHeatRow)
      }).catch(() => {
        this.planData = []
        this.$message.error('计划执行热度加载失败')
      }).finally(() => {
        this.planLoading = false
      })
    },
    buildDetailParams () {
      const start = this.detailDateRange && this.detailDateRange[0]
      const end = this.detailDateRange && this.detailDateRange[1]
      const trainNumber = this.detailQuery.trainNumber ? String(this.detailQuery.trainNumber).trim() : ''
      const useDateRange = !trainNumber || this.detailDateChanged
      return {
        ...this.detailQuery,
        trainNumber,
        startTime: useDateRange && start ? `${start} 00:00:00` : '',
        endTime: useDateRange && end ? `${end} 23:59:59` : '',
        page: this.detailPage.currentPage,
        size: this.detailPage.pageSize
      }
    },
    loadDetails () {
      this.detailLoading = true
      return weightPlan({
        page: this.detailPage.currentPage,
        size: this.detailPage.pageSize
      }, this.buildDetailParams()).then(res => {
        const data = (res.data && res.data.data) || {}
        this.detailData = data.records || []
        this.detailPage.total = data.total || 0
      }).catch(() => {
        this.detailData = []
        this.detailPage.total = 0
        this.$message.error('过车明细加载失败')
      }).finally(() => {
        this.detailLoading = false
      })
    },
    manualRefresh () {
      this.loadAll()
    },
    startAutoRefresh () {
      this.clearAutoRefresh()
      this.refreshTimer = window.setInterval(() => {
        if (this.autoRefresh) {
          this.loadAll()
        }
      }, this.refreshIntervalSeconds * 1000)
    },
    clearAutoRefresh () {
      if (this.refreshTimer) {
        window.clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },
    toggleAutoRefresh () {
      this.autoRefresh = !this.autoRefresh
    },
    searchPounds () {
      this.poundPage.currentPage = 1
      this.loadPounds()
    },
    poundCurrentChange (currentPage) {
      this.poundPage.currentPage = currentPage
      this.loadPounds()
    },
    poundSizeChange (pageSize) {
      this.poundPage.pageSize = pageSize
      this.poundPage.currentPage = 1
      this.loadPounds()
    },
    searchDetails () {
      this.detailPage.currentPage = 1
      this.loadDetails()
    },
    handleDetailDateChange () {
      this.detailDateChanged = true
      this.searchDetails()
    },
    resetDetails () {
      this.detailDateRange = [
        getSettingDate('daySLast7', { format: 'YYYY-MM-DD' }),
        getSettingDate('dayE', { format: 'YYYY-MM-DD' })
      ]
      this.detailDateChanged = false
      this.detailQuery = {
        custNo: '',
        trainNumber: '',
        cargoNo: ''
      }
      this.searchDetails()
    },
    detailCurrentChange (currentPage) {
      this.detailPage.currentPage = currentPage
      this.loadDetails()
    },
    detailSizeChange (pageSize) {
      this.detailPage.pageSize = pageSize
      this.detailPage.currentPage = 1
      this.loadDetails()
    },
    remoteMethod (query) {
      this.dictLoading = true
      pycode({ varName: query }).then(res => {
        this.options = unwrapDictList(res)
      }).catch(() => {
        this.options = []
      }).finally(() => {
        this.dictLoading = false
      })
    },
    formatNumber (value) {
      const num = Number(value || 0)
      return num.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    formatRate (value) {
      const num = Number(value || 0)
      return num.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    progressPercent (value) {
      const num = Number(value || 0)
      return Math.max(0, Math.min(100, Number(num.toFixed(2))))
    },
    calcPercent (value, total) {
      const n = Number(value || 0)
      const d = Number(total || 0)
      if (!d) return 0
      return Number(((n / d) * 100).toFixed(2))
    },
    calcComparePercent (current, previous) {
      const currentValue = Number(current || 0)
      const previousValue = Number(previous || 0)
      if (!previousValue) return 0
      return Number((((currentValue - previousValue) / previousValue) * 100).toFixed(2))
    },
    calcAvgStayMinutes (records) {
      const values = (records || [])
        .map(item => Number(item.plantStaySeconds || 0))
        .filter(value => value > 0)
      if (!values.length) return 0
      const total = values.reduce((sum, value) => sum + value, 0)
      return Number((total / values.length / 60).toFixed(2))
    },
    normalizeRefreshIntervalSeconds (value) {
      const seconds = Number(value)
      if (!Number.isFinite(seconds)) {
        return DEFAULT_REFRESH_INTERVAL_SECONDS
      }
      return Math.max(
        MIN_REFRESH_INTERVAL_SECONDS,
        Math.min(MAX_REFRESH_INTERVAL_SECONDS, Math.floor(seconds))
      )
    },
    buildPlanHeatRow (row) {
      const planTotalWeight = Number(row.planTotalWeight || 0)
      const doneWeight = Number(row.planExecuteWeight || row.doneWeight || 0)
      const completeRate = this.calcPercent(doneWeight, planTotalWeight)
      const timeProgress = this.calcTimeProgress(row.intervalStartTime, row.intervalEndTime)
      const next = {
        ...row,
        ownerName: row.ownerName || row.custName || row.custNo || '-',
        doneWeight,
        completeRate,
        timeProgress
      }
      next.riskLevel = this.calcRiskLevel(next)
      return next
    },
    calcTimeProgress (startTime, endTime) {
      const start = this.parseTime(startTime)
      const end = this.parseTime(endTime)
      const now = Date.now()
      if (!start || !end || end <= start) return 0
      if (now <= start) return 0
      if (now >= end) return 100
      return Number((((now - start) / (end - start)) * 100).toFixed(2))
    },
    calcRiskLevel (row) {
      const planTotalWeight = Number(row.planTotalWeight || 0)
      const completeRate = Number(row.completeRate || 0)
      const start = this.parseTime(row.intervalStartTime)
      const end = this.parseTime(row.intervalEndTime)
      const now = Date.now()
      if (planTotalWeight <= 0) return 'focus'
      if (completeRate >= 95) return 'normal'
      if (start && now < start) return 'normal'
      if (end && now > end) return 'lag'
      const gap = Number(row.timeProgress || 0) - completeRate
      if (gap <= 10) return 'normal'
      if (gap <= 25) return 'focus'
      return 'lag'
    },
    parseTime (value) {
      if (!value) return 0
      if (typeof value === 'number') return value
      const time = Date.parse(String(value).replace(/-/g, '/'))
      return Number.isNaN(time) ? 0 : time
    },
    trendText (value, unit = '%') {
      const num = Number(value || 0)
      const prefix = num > 0 ? '↑ ' : num < 0 ? '↓ ' : ''
      return `${prefix}${Math.abs(num).toFixed(2)}${unit}`
    },
    trendClass (value, reverse = false) {
      const num = Number(value || 0)
      if (num === 0) return 'trend-flat'
      const positive = num > 0
      return positive === !reverse ? 'trend-up' : 'trend-down'
    },
    timeText (value) {
      if (!value) return '-'
      return String(value).slice(0, 19)
    },
    statusText (value) {
      const map = {
        busy: '繁忙',
        normal: '正常',
        focus: '关注',
        idle: '空闲'
      }
      return map[value] || '空闲'
    },
    statusType (value) {
      const map = {
        busy: 'warning',
        normal: 'success',
        focus: 'danger',
        idle: 'info'
      }
      return map[value] || 'info'
    },
    riskText (value) {
      const map = {
        normal: '正常',
        focus: '关注',
        lag: '滞后'
      }
      return map[value] || '关注'
    },
    riskType (value) {
      const map = {
        normal: 'success',
        focus: 'warning',
        lag: 'danger'
      }
      return map[value] || 'warning'
    },
    stayText (seconds) {
      if (seconds === null || seconds === undefined || seconds === '') return '-'
      const sec = Number(seconds)
      if (Number.isNaN(sec)) return '-'
      const minutes = Math.floor(sec / 60)
      if (minutes < 60) return `${minutes}分钟`
      const hours = Math.floor(minutes / 60)
      return `${hours}小时${minutes % 60}分钟`
    },
    vehicleStatusText (value) {
      const map = {
        0: '未开始',
        1: '进行中',
        2: '已完成'
      }
      return map[value] || '进行中'
    },
    vehicleStatusType (value) {
      const map = {
        0: 'info',
        1: 'warning',
        2: 'success'
      }
      return map[value] || 'warning'
    }
  }
}
</script>

<style scoped lang="scss">
.daily-shipment-monitoring {
  min-height: 100%;
  padding: 18px 20px 24px;
  background: #f5f7fb;
  color: #1f2937;
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 58px;
  padding: 0 18px;
  background: #fff;
  border: 1px solid #e5e8ef;
  border-radius: 8px;
}

.title {
  font-size: 18px;
  font-weight: 700;
}

.subtitle {
  margin-top: 4px;
  color: #6b7280;
  font-size: 12px;
}

.top-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: 14px;
  font-size: 12px;
  white-space: nowrap;

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
  }
}

.status-pill.is-running {
  color: #047857;
  background: #d1fae5;

  .dot {
    background: #10b981;
  }
}

.status-pill.is-paused {
  color: #92400e;
  background: #fef3c7;

  .dot {
    background: #f59e0b;
  }
}

.metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-top: 14px;
  overflow: visible;
}

.metric-card {
  position: relative;
  border-radius: 8px;
  transform-origin: center;
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
  will-change: transform;

  &:hover {
    z-index: 2;
    border-color: #d7e2f5;
    box-shadow: 0 12px 28px rgba(37, 99, 235, 0.14);
    transform: translateY(-3px) scale(1.025);
  }
}

.metric-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #6b7280;
  font-size: 13px;
}

.metric-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: #fff;
  font-weight: 700;
}

.blue { background: #2563eb; }
.green { background: #16a34a; }
.orange { background: #d97706; }
.cyan { background: #0891b2; }

.metric-value {
  margin-top: 12px;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: 0;

  span {
    margin-left: 4px;
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
  }
}

.metric-foot {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  color: #6b7280;
  font-size: 12px;
}

.trend-up {
  color: #16a34a;
  font-weight: 700;
}

.trend-down {
  color: #dc2626;
  font-weight: 700;
}

.trend-flat {
  color: #6b7280;
  font-weight: 700;
}

.main-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(0, 0.92fr);
  align-items: start;
  gap: 14px;
  margin-top: 14px;
}

.panel-card {
  border-radius: 8px;

  ::v-deep .el-card__header {
    padding: 12px 16px;
  }
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.panel-title {
  font-size: 16px;
  font-weight: 700;
  white-space: nowrap;
}

.panel-tools,
.detail-filters {
  display: flex;
  align-items: center;
  gap: 8px;
}

.panel-tools .el-input {
  width: 230px;
}

.pound-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.pound-card {
  padding: 12px;
  border: 1px solid #e5e8ef;
  border-radius: 8px;
  background: #fbfdff;
}

.pound-name {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-weight: 700;

  span:first-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.pound-meta {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  color: #6b7280;
  font-size: 12px;

  strong {
    color: #1f2937;
  }
}

.plan-heat-table {
  ::v-deep .el-table__cell {
    padding: 10px 0;
  }

  ::v-deep .cell {
    line-height: 20px;
  }

  ::v-deep .plan-progress-column .cell {
    display: flex;
    justify-content: center;
    align-items: center;
    line-height: 1;
  }
}

.progress-cell {
  display: grid;
  grid-template-columns: 124px 54px;
  align-items: center;
  justify-content: center;
  width: 188px;
  min-height: 20px;
  column-gap: 8px;
  box-sizing: border-box;

  .el-progress {
    width: 100%;
    line-height: 1;
  }

  ::v-deep .el-progress-bar {
    padding-right: 0;
    margin-right: 0;
  }

  ::v-deep .el-progress-bar__outer {
    vertical-align: middle;
  }

  span {
    color: #1f2937;
    font-size: 13px;
    line-height: 20px;
    text-align: right;
    white-space: nowrap;
  }
}

.detail-panel {
  margin-top: 14px;
}

.detail-head {
  align-items: flex-start;
}

.detail-filters {
  flex-wrap: wrap;
  justify-content: flex-end;

  .el-select,
  .el-input {
    width: 150px;
  }
}

.table-pagination {
  margin-top: 12px;
  text-align: right;
}

@media (max-width: 1366px) {
  .metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .main-layout {
    grid-template-columns: 1fr;
  }

  .detail-head {
    display: block;
  }

  .detail-filters {
    justify-content: flex-start;
    margin-top: 12px;
  }
}
</style>
